/// <amd-module name="WorkItemsHub/Utils/Clipboard" />
import * as React from "react";
import { LegacyComponent } from 'VSS/Legacy/Legacy';
import { IVssLayoutManager } from 'VSS/Platform/Layout';
import { IVssPageContext } from 'VSS/Platform/Context';

const FORMAT_TEXT = "text";

/**
 * Copies the specified data in HTML format using the old execCommand("copy") API
 * 
 * @param data The HTML string to copy.
 * @param copyAsHtml Whether or not copy the data as html.
 */
function nativeCopy(data: string, copyAsHtml: boolean): boolean {
    let success = false;

    if (!copyAsHtml && (<any>window).clipboardData !== undefined) {
        (<any>window).clipboardData.setData(FORMAT_TEXT, data);
        success = true;
    }
    else {
        let range, sel;
        // Create an element in the dom with the content to be copied.
        let copyContent = document.createElement("div");
        const copyDivId = "copyDiv";
        copyContent.id = copyDivId;
        try {

            //body can have its own background color.
            copyContent.style.backgroundColor = "white";

            if (copyAsHtml) {
                copyContent.innerHTML += data;
            }
            else {
                copyContent.style.whiteSpace = "pre";
                copyContent.textContent = data;
            }

            if ((<any>document.body).createTextRange) {
                let body = document.getElementsByTagName("body")[0];
                body.insertBefore(copyContent, body.firstChild);

                range = (<any>document.body).createTextRange();
                range.moveToElementText(document.getElementById(copyDivId));
                range.select();
                success = range.execCommand("copy");
            }
            else if (document.createRange && window.getSelection) {
                let body = document.getElementsByTagName("body")[0];
                body.appendChild(copyContent);

                range = document.createRange();
                sel = window.getSelection();
                sel.removeAllRanges();

                range.selectNodeContents(document.getElementById(copyDivId));
                sel.addRange(range);
                success = (<any>document).execCommand("copy");
            }
        }
        finally {
            // Remove the content from the dom.
            // Note: IE11 doesn't support remove()
            copyContent.parentElement.removeChild(copyContent);
        }
    }

    return success;
}

/**
 * To support non-IE browser copy, opens a new popup window and writes the table to the window allowing the user to copy manually.
 * 
 * @param data The data to place on the clipboard (via a popup window).
 */
function copyUsingNewWindow(pageContext: IVssPageContext, data: string, options?: IClipboardOptions) { 
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    layoutManager.renderCallout(() => {
        return React.createElement(LegacyComponent, {
            modules: ["WorkItemsHub/Scripts/LegacyPlatform/CopyContentDialog"],
            wrappedType: "copyContentDialog",
            data: data, 
            copyAsHtml: options.copyAsHtml
        }, null);
    });
}

/**
 * Copies the specified data to the clipboard in the TEXT format using a progressively degrading experience.
 * 
 * @param data The data to copy.
 */
export function copyToClipboard(pageContext: IVssPageContext, data: string, options?: IClipboardOptions) {
    if (data && typeof data === "string") {
        if (options && options.showCopyDialog) {
            // force show copy dialog to avoid confusing behavior in certain use cases
            copyUsingNewWindow(pageContext, data, options);
        }
        else {
            var dataCopied = false;
            if (options && options.copyAsHtml) {
                // HTML Copy
                if (supportsNativeHtmlCopy()) {
                    try {
                        dataCopied = nativeCopy(data, true);
                    }
                    catch (ex) { }
                }
            }
            else {
                // Plain text copy
                if (supportsNativeCopy()) {
                    try {
                        dataCopied = nativeCopy(data, false);
                    }
                    catch (ex) { }
                }
            }

            if (!dataCopied) {
                copyUsingNewWindow(pageContext, data, options);
            }
        }
    }
}

/**
 * Gets a boolean value indicating whether the current browser supports native clipboard access.
 */
export function supportsNativeCopy(): boolean {
    return document.queryCommandSupported("copy") || (<any>window).clipboardData !== undefined;
}

/**
 * Gets a boolean value indicating whether the current browser supports native clipboard access for HTML content.
 */
export function supportsNativeHtmlCopy(): boolean {
    return (<any>document.body).createTextRange !== undefined
        || (document.queryCommandSupported("copy") && document.createRange !== undefined);
}

/**
 * Options for Copy To Clipboard feature
 */
export interface IClipboardOptions {
    /**
     * Boolean specifying whether the data should be copied as plain text or html
     */
    copyAsHtml?: boolean;

    /**
     * Option for always using copy to clipboard dialog or browser native feature (if present)
     */
    showCopyDialog?: boolean;
}
